// DemandesDevisPage.jsx — Gestion des demandes de devis pour artisans
// Système: Maximum 5 artisans peuvent accepter par projet
// Coordonnées client masquées jusqu'à acceptation
// Pénalité après 2 non-envois de devis

import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  FileText, MapPin, Clock, Euro, Phone, Mail, User, 
  CheckCircle, XCircle, AlertTriangle, Eye, EyeOff,
  Calendar, Briefcase, ChevronRight, Send, Lock, Unlock,
  AlertOctagon, Timer, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Composant Badge de statut
const StatusBadge = ({ status, acceptedCount }) => {
  const configs = {
    notified: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Nouvelle demande', icon: Eye },
    accepted: { bg: 'bg-green-100', text: 'text-green-700', label: 'Acceptée - En attente devis', icon: Clock },
    quote_sent: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Devis envoyé', icon: CheckCircle },
    declined: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Refusée', icon: XCircle },
    penalized: { bg: 'bg-red-100', text: 'text-red-700', label: 'Pénalisé', icon: AlertOctagon },
  };
  
  const config = configs[status] || configs.notified;
  const Icon = config.icon;
  
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon size={12} />
        {config.label}
      </span>
      {acceptedCount !== undefined && (
        <span className="text-xs text-gray-500">
          {acceptedCount}/5 acceptés
        </span>
      )}
    </div>
  );
};

// Composant Carte de demande
const DemandeCard = ({ demande, onAccept, onDecline, onQuoteSent, isLoading }) => {
  const [expanded, setExpanded] = useState(false);
  const hasAccepted = demande.my_status === 'accepted' || demande.my_status === 'quote_sent';
  const canAccept = demande.can_accept && demande.my_status === 'notified';
  const isFull = demande.accepted_count >= 5;
  
  // Calcul du temps restant si deadline
  const getTimeRemaining = () => {
    if (!demande.deadline_at) return null;
    const deadline = new Date(demande.deadline_at);
    const now = new Date();
    const diff = deadline - now;
    if (diff <= 0) return 'Délai dépassé';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}min restantes`;
  };
  
  const budgetLabels = {
    'moins_500': 'Moins de 500€',
    '500_1500': '500€ - 1 500€',
    '1500_5000': '1 500€ - 5 000€',
    '5000_15000': '5 000€ - 15 000€',
    '15000_50000': '15 000€ - 50 000€',
    'plus_50000': 'Plus de 50 000€',
    'non_defini': 'À définir'
  };
  
  const urgencyLabels = {
    'low': 'Pas urgent',
    'medium': 'Sous quelques semaines',
    'high': 'Sous quelques jours',
    'urgent': 'Urgent - Immédiat'
  };
  
  const urgencyColors = {
    'low': 'text-gray-500',
    'medium': 'text-blue-600',
    'high': 'text-orange-600',
    'urgent': 'text-red-600'
  };

  return (
    <div className={`bg-white rounded-xl border ${hasAccepted ? 'border-green-200' : 'border-gray-200'} shadow-sm overflow-hidden transition-all`}>
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase size={16} className="text-[#1E3A5F] flex-shrink-0" />
              <span className="font-semibold text-gray-900 capitalize">
                {demande.metier_label || demande.metier}
              </span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <MapPin size={14} className="flex-shrink-0" />
              <span>{demande.city}</span>
              {hasAccepted && demande.full_postal_code && (
                <span className="text-gray-400">({demande.full_postal_code})</span>
              )}
            </div>
            
            <p className="text-sm text-gray-700 line-clamp-2">
              {demande.description}
            </p>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={demande.my_status} acceptedCount={demande.accepted_count} />
            <ChevronRight 
              size={20} 
              className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} 
            />
          </div>
        </div>
        
        {/* Infos rapides */}
        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100">
          {demande.budget && demande.budget !== 'non_defini' && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              <Euro size={12} />
              {budgetLabels[demande.budget] || demande.budget}
            </span>
          )}
          {demande.urgency && (
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 ${urgencyColors[demande.urgency]}`}>
              <Clock size={12} />
              {urgencyLabels[demande.urgency]}
            </span>
          )}
          {demande.created_at && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={12} />
              {new Date(demande.created_at).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
      </div>
      
      {/* Contenu étendu */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Description complète */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Description du projet</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {demande.description}
            </p>
          </div>
          
          {/* Photos si présentes */}
          {demande.photos && demande.photos.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Photos</h4>
              <div className="flex gap-2 overflow-x-auto">
                {demande.photos.map((photo, idx) => (
                  <img 
                    key={idx}
                    src={photo.url}
                    alt={`Photo ${idx + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Coordonnées client - MASQUÉES ou VISIBLES */}
          <div className="mt-4 p-4 rounded-lg bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              {hasAccepted ? (
                <>
                  <Unlock size={16} className="text-green-600" />
                  <h4 className="text-sm font-semibold text-green-700">Coordonnées du client</h4>
                </>
              ) : (
                <>
                  <Lock size={16} className="text-gray-400" />
                  <h4 className="text-sm font-semibold text-gray-500">Coordonnées masquées</h4>
                </>
              )}
            </div>
            
            {hasAccepted ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User size={14} className="text-gray-500" />
                  <span className="font-medium">{demande.client_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-gray-500" />
                  <a href={`tel:${demande.client_phone}`} className="text-[#1E3A5F] hover:underline">
                    {demande.client_phone}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail size={14} className="text-gray-500" />
                  <a href={`mailto:${demande.client_email}`} className="text-[#1E3A5F] hover:underline">
                    {demande.client_email}
                  </a>
                </div>
                {demande.address && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={14} className="text-gray-500" />
                    <span>{demande.address}, {demande.full_postal_code} {demande.city}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <User size={14} />
                  <span className="blur-sm select-none">Jean-Pierre Martin</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={14} />
                  <span className="blur-sm select-none">06 ** ** ** **</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={14} />
                  <span className="blur-sm select-none">******@******.***</span>
                </div>
                <p className="text-xs mt-2 text-gray-500">
                  Accepte cette demande pour voir les coordonnées du client.
                </p>
              </div>
            )}
          </div>
          
          {/* Deadline si accepté */}
          {hasAccepted && demande.my_status === 'accepted' && demande.deadline_at && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700">
                <Timer size={16} />
                <span className="text-sm font-medium">
                  Délai pour envoyer ton devis : {getTimeRemaining()}
                </span>
              </div>
              <p className="text-xs text-orange-600 mt-1">
                Attention : 2 non-envois de devis après acceptation = blocage des demandes.
              </p>
            </div>
          )}
          
          {/* Boutons d'action */}
          <div className="mt-4 flex flex-wrap gap-3">
            {canAccept && (
              <button
                onClick={() => onAccept(demande.id)}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                <CheckCircle size={18} />
                Je m'en occupe
              </button>
            )}
            
            {canAccept && (
              <button
                onClick={() => onDecline(demande.id)}
                disabled={isLoading}
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                <XCircle size={18} />
              </button>
            )}
            
            {demande.my_status === 'accepted' && (
              <button
                onClick={() => onQuoteSent(demande.id)}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                <Send size={18} />
                J'ai envoyé mon devis
              </button>
            )}
            
            {isFull && !hasAccepted && (
              <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-500 rounded-xl">
                <AlertTriangle size={18} />
                5 artisans ont déjà accepté
              </div>
            )}
            
            {demande.my_status === 'quote_sent' && (
              <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-100 text-emerald-700 rounded-xl">
                <CheckCircle size={18} />
                Devis envoyé - En attente du client
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal
export default function DemandesDevisPage() {
  // Récupérer le contexte du layout (seller_id, user, etc.)
  const context = useOutletContext() || {};
  const { sellerProfile, user } = context;
  
  // Déterminer le seller_id
  const sellerId = sellerProfile?.seller_id || sellerProfile?.id || user?.id || 'demo-seller';
  const isDemo = !sellerProfile?.seller_id;
  
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [penaltyStats, setPenaltyStats] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, accepted, completed
  
  // Refs et états pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Gérer le scroll des filtres
  const handleScrollFilters = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  // Charger les demandes
  const loadDemandes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/projects/artisan/${sellerId}`);
      const data = await response.json();
      
      if (data.blocked) {
        setError(data.message);
        setDemandes([]);
      } else {
        const projects = data.projects || [];
        // Si aucune demande réelle, afficher les données de démo
        if (projects.length === 0) {
          setDemandes(generateDemoData());
        } else {
          setDemandes(projects);
        }
        setError(null);
      }
    } catch (err) {
      console.error('Erreur chargement demandes:', err);
      // En mode démo ou erreur, générer des données fictives
      setDemandes(generateDemoData());
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les stats de pénalités
  const loadPenaltyStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/artisan/${sellerId}/penalty-stats`);
      const data = await response.json();
      setPenaltyStats(data);
    } catch (err) {
      console.error('Erreur chargement stats pénalités:', err);
    }
  };
  
  useEffect(() => {
    if (sellerId) {
      loadDemandes();
      loadPenaltyStats();
    }
  }, [sellerId]);
  
  // Données de démo
  const generateDemoData = () => [
    {
      id: 'demo-1',
      city: 'Marseille',
      postal_code: '13***',
      metier: 'plomberie',
      metier_label: 'Plomberie',
      description: 'Fuite sous l\'évier de la cuisine. Le robinet goutte également. J\'aimerais un devis pour la réparation complète.',
      budget: '500_1500',
      urgency: 'high',
      created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      accepted_count: 2,
      max_acceptances: 5,
      can_accept: true,
      my_status: 'notified',
      photos: []
    },
    {
      id: 'demo-2',
      city: 'Aix-en-Provence',
      postal_code: '13***',
      full_postal_code: '13100',
      metier: 'electricite',
      metier_label: 'Électricité',
      description: 'Installation complète tableau électrique aux normes NF C 15-100. Maison de 120m², 4 chambres.',
      budget: '5000_15000',
      urgency: 'medium',
      created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      accepted_count: 4,
      max_acceptances: 5,
      can_accept: false,
      my_status: 'accepted',
      client_name: 'Marie Dupont',
      client_phone: '06 12 34 56 78',
      client_email: 'marie.dupont@email.com',
      address: '15 rue des Oliviers',
      deadline_at: new Date(Date.now() + 36 * 60 * 60 * 1000).toISOString(),
      photos: []
    },
    {
      id: 'demo-3',
      city: 'Nice',
      postal_code: '06***',
      full_postal_code: '06000',
      metier: 'piscine',
      metier_label: 'Piscine',
      description: 'Rénovation liner piscine 8x4m. Changement du système de filtration. Devis urgent demandé.',
      budget: '5000_15000',
      urgency: 'urgent',
      created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      accepted_count: 5,
      max_acceptances: 5,
      can_accept: false,
      my_status: 'quote_sent',
      client_name: 'Pierre Martin',
      client_phone: '06 98 76 54 32',
      client_email: 'p.martin@email.com',
      photos: []
    }
  ];
  
  // Actions
  const handleAccept = async (projectId) => {
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/accept?seller_id=${sellerId}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (response.ok) {
        // Recharger les demandes pour avoir les coordonnées
        await loadDemandes();
        alert(`Demande acceptée !\n\nCoordonnées du client :\n${data.client.name}\n${data.client.phone}\n${data.client.email}\n\nTu as 48h pour envoyer ton devis.`);
      } else {
        alert(data.detail || 'Erreur lors de l\'acceptation');
      }
    } catch (err) {
      console.error('Erreur acceptation:', err);
      // Démo: simuler l'acceptation
      if (isDemo) {
        setDemandes(prev => prev.map(d => 
          d.id === projectId 
            ? { 
                ...d, 
                my_status: 'accepted', 
                can_accept: false,
                client_name: 'Client Démo',
                client_phone: '06 00 00 00 00',
                client_email: 'demo@client.com',
                deadline_at: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString()
              } 
            : d
        ));
      }
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDecline = async (projectId) => {
    if (!window.confirm('Refuser cette demande ?')) return;
    
    setActionLoading(true);
    try {
      await fetch(
        `${API_URL}/api/projects/${projectId}/decline?seller_id=${sellerId}`,
        { method: 'POST' }
      );
      await loadDemandes();
    } catch (err) {
      console.error('Erreur refus:', err);
      // Démo
      if (isDemo) {
        setDemandes(prev => prev.map(d => 
          d.id === projectId ? { ...d, my_status: 'declined' } : d
        ));
      }
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleQuoteSent = async (projectId) => {
    setActionLoading(true);
    try {
      await fetch(
        `${API_URL}/api/projects/${projectId}/quote-sent?seller_id=${sellerId}`,
        { method: 'POST' }
      );
      await loadDemandes();
      alert('Devis marqué comme envoyé !');
    } catch (err) {
      console.error('Erreur:', err);
      // Démo
      if (isDemo) {
        setDemandes(prev => prev.map(d => 
          d.id === projectId ? { ...d, my_status: 'quote_sent' } : d
        ));
      }
    } finally {
      setActionLoading(false);
    }
  };
  
  // Filtrer les demandes
  const filteredDemandes = demandes.filter(d => {
    if (filter === 'all') return true;
    if (filter === 'pending') return d.my_status === 'notified';
    if (filter === 'accepted') return d.my_status === 'accepted';
    if (filter === 'completed') return d.my_status === 'quote_sent';
    return true;
  });
  
  // Stats rapides
  const stats = {
    total: demandes.length,
    pending: demandes.filter(d => d.my_status === 'notified').length,
    accepted: demandes.filter(d => d.my_status === 'accepted').length,
    completed: demandes.filter(d => d.my_status === 'quote_sent').length
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D4A6F] text-white p-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText size={24} />
          Demandes de devis
        </h1>
        <p className="text-white/80 mt-1 text-sm">
          Reçois des demandes de clients près de chez toi
        </p>
        
        {/* Alerte pénalité si applicable */}
        {penaltyStats && penaltyStats.penalty_count > 0 && (
          <div className="mt-4 p-3 bg-red-500/20 border border-red-300/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} />
              <span className="text-sm font-medium">
                {penaltyStats.penalty_count}/2 pénalité(s)
              </span>
            </div>
            <p className="text-xs mt-1 text-white/70">
              {penaltyStats.blocked 
                ? 'Accès bloqué aux nouvelles demandes.'
                : 'Attention : 2 pénalités = blocage des demandes.'}
            </p>
          </div>
        )}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 p-4 -mt-4">
        {[
          { label: 'Total', value: stats.total, color: 'bg-gray-100 text-gray-700' },
          { label: 'Nouvelles', value: stats.pending, color: 'bg-blue-100 text-blue-700' },
          { label: 'En cours', value: stats.accepted, color: 'bg-orange-100 text-orange-700' },
          { label: 'Envoyés', value: stats.completed, color: 'bg-green-100 text-green-700' },
        ].map((stat, i) => (
          <div key={i} className={`${stat.color} rounded-xl p-3 text-center`}>
            <div className="text-2xl font-bold">{stat.value}</div>
            <div className="text-xs">{stat.label}</div>
          </div>
        ))}
      </div>
      
      {/* Filtres - Style premium */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          {/* Filtres Mobile */}
          <div className="sm:hidden">
            <div className="relative flex items-center">
              <div 
                ref={scrollContainerRef}
                onScroll={handleScrollFilters}
                className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {[
                  { id: 'all', label: 'Toutes', icon: null },
                  { id: 'pending', label: 'Nouvelles', icon: Eye },
                  { id: 'accepted', label: 'En cours', icon: Clock },
                  { id: 'completed', label: 'Devis envoyés', icon: CheckCircle },
                ].map((f) => {
                  const FilterIcon = f.icon;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFilter(f.id)}
                      data-testid={`filter-${f.id}`}
                      className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                        filter === f.id
                          ? 'bg-orange-500 text-white shadow-sm'
                          : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {FilterIcon && <FilterIcon size={12} />}
                      {f.label}
                    </button>
                  );
                })}
              </div>
              
              {/* Flèche orange discrète */}
              {showScrollHint && (
                <div className="flex-shrink-0 flex items-center pointer-events-none">
                  <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
                </div>
              )}
            </div>
          </div>

          {/* Filtres Desktop */}
          <div className="hidden sm:flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Toutes', icon: null },
              { id: 'pending', label: 'Nouvelles', icon: Eye },
              { id: 'accepted', label: 'En cours', icon: Clock },
              { id: 'completed', label: 'Devis envoyés', icon: CheckCircle },
            ].map((f) => {
              const FilterIcon = f.icon;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                    filter === f.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {FilterIcon && <FilterIcon size={14} />}
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="px-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertOctagon size={48} className="mx-auto text-red-500 mb-3" />
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        ) : filteredDemandes.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucune demande pour le moment</p>
            <p className="text-sm text-gray-400 mt-1">
              Les demandes de clients dans ta zone apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDemandes.map(demande => (
              <DemandeCard
                key={demande.id}
                demande={demande}
                onAccept={handleAccept}
                onDecline={handleDecline}
                onQuoteSent={handleQuoteSent}
                isLoading={actionLoading}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Info box */}
      <div className="mx-4 mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-3">
          <Award size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-800 text-sm">Comment ça marche ?</h4>
            <ul className="text-xs text-blue-700 mt-2 space-y-1">
              <li>1. Tu reçois des demandes de clients dans ta zone</li>
              <li>2. Clique sur "Je m'en occupe" pour voir les coordonnées</li>
              <li>3. Contacte le client et envoie ton devis sous 48h</li>
              <li>4. Maximum 5 artisans peuvent accepter par demande</li>
              <li>5. Aucun frais - Tu ne paies que si tu gagnes le chantier</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
